<template>
    <div data-cy="listContainer">
        <Toolbar
            :analytics-prefix="`${analyticsPrefix} List`"
            :hide-search="hideSearchInput"
            @text-search="textSearch($event)"
        >
            <template slot="action-buttons">
                <!-- Passed down to the Toolbar component -->
                <slot name="action-buttons" />
            </template>

            <template slot="filter-buttons">
                <!-- Passed down to the Toolbar component -->
                <slot name="filter-buttons" />
            </template>
        </Toolbar>

        <div class="row">
            <div class="col-12 px-0">
                <div
                    :class="['list', {loading}]"
                    data-cy="list"
                >
                    <div
                        :class="['row', 'mx-0', 'sticky-top', {'sticky-top-offset': stickyHeaderOffset}]"
                    >
                        <div
                            class="col-12 px-0 search-filter-list"
                            data-cy="listSearchFilter"
                        >
                            <div
                                v-if="!isEmpty(combinedSearchFields)"
                                id="searchFilteredBy"
                                class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center px-2"
                                data-cy="searchFilteredBy"
                            >
                                <em>Search results filtered by: </em>

                                <div
                                    v-for="(field, key, index) in combinedSearchFields"
                                    :key="key"
                                    class="search-filter-item"
                                    :data-cy="camelCase(`listSearchFilterItem ${key}`)"
                                >
                                    <span
                                        class="search-filter-type"
                                        data-cy="listSearchFilterKey"
                                    >
                                        {{ tooltipWording.search_field_names[key] }}
                                    </span>

                                    <span
                                        class="search-filter-values"
                                        data-cy="listSearchFilterValue"
                                    >
                                        <template v-if="Array.isArray(field)">
                                            <template
                                                v-for="(val, idx) in field"
                                            >
                                                <span :key="val + index">
                                                    {{ getDisplay(key, val) }}<span v-if="idx + 1 !== field.length"> OR </span>
                                                </span>
                                            </template>
                                        </template>
                                        <span v-else>
                                            {{ getDisplay(key, field) }}
                                        </span>
                                    </span>

                                    <span v-if="index + 1 !== Object.keys(combinedSearchFields).length"> AND </span>
                                </div>

                                <button
                                    class="btn btn-link clear-filters px-0 px-lg-2"
                                    data-analytics-label="Clear Filters Button"
                                    :data-analytics-location="`${analyticsPrefix} List View`"
                                    data-cy="listSearchFilterClearButton"
                                    @click="clearSearchValues"
                                >
                                    <FontAwesomeIcon
                                        far
                                        icon="times"
                                    />
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        <div
                            class="list-header col-12 px-0"
                            data-cy="listHeader"
                        >
                            <div
                                v-for="column in columns"
                                :key="column.header"
                                :class="[{'sortable-column-heading': column.sortable, 'not-sortable-column-heading': !column.sortable}, column.class]"
                                :data-cy="camelCase(`listHeaderColumn ${column.key}`)"
                                @click="sortColumn(column)"
                            >
                                <template v-if="column.sortable">
                                    {{ startCase(column.key) }}
                                    <FontAwesomeIcon
                                        v-if="sort.includes(column.sortBy || column.key)"
                                        fas
                                        icon="caret-up"
                                        :class="['sort-direction', {'descending': sort === `-${column.sortBy || column.key}`}]"
                                        :data-cy="camelCase(`listHeaderColumnSort ${column.key}`)"
                                    />
                                </template>

                                <template v-else>
                                    <template v-if="column.key === 'options'">
                                        <span
                                            v-if="fetching"
                                            id="listHeaderFetchingIndicator"
                                            class="align-self-center font-weight-normal"
                                            data-cy="listHeaderFetchingIndicator"
                                        >
                                            <FontAwesomeIcon
                                                icon="spinner"
                                                pulse
                                                data-cy="listFetchingIndicatorIcon"
                                            />
                                            loading...
                                        </span>
                                    </template>
                                    <template v-else>
                                        {{ startCase(column.key) }}
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div
                        :class="['loading-indicator', { 'd-none': !loading }]"
                        data-cy="listLoadingIndicator"
                    >
                        <FontAwesomeIcon
                            far
                            icon="spinner"
                            pulse
                        />
                        loading...
                    </div>

                    <div
                        v-if="!loading"
                        class="list-items"
                        data-cy="listItems"
                    >
                        <LListItem
                            v-for="item in items"
                            :key="item.id"
                            :columns="columns"
                            :item="item"
                        >
                            <template
                                v-for="column in columns"
                                #[`cell(${column.key})`]
                            >
                                <slot
                                    :name="`cell(${column.key})`"
                                    :item="item"
                                />
                            </template>

                            <template slot="list-item-action-buttons">
                                <!-- Passed down to the ListItem component. Passes `item` as Slot Prop -->
                                <slot
                                    name="list-item-action-buttons"
                                    :item="item"
                                />
                            </template>
                        </LListItem>
                    </div>

                    <div
                        v-cloak
                        v-if="!totalItemCount && !loading"
                        class="list-empty"
                        data-cy="listEmpty"
                    >
                        <h5
                            v-if="Object.keys(combinedSearchFields).length"
                            class="text-muted"
                        >
                            {{ emptySearchMessage || `No ${ modelName } match the given search parameters.` }}
                        </h5>
                        <h5
                            v-else
                            class="text-muted"
                        >
                            {{ emptyMessage || `No ${ modelName } exist. Create one to get started!` }}
                        </h5>
                    </div>
                </div>
            </div>
        </div>

        <LFooter
            v-if="(!loading && totalItemCount)"
            :analytics-prefix="`${analyticsPrefix} List`"
            :fetching="fetching"
            :page="page"
            :page-query-param="pageQueryParam"
            :per-page="perPage"
            :per-page-query-param="perPageQueryParam"
            :total-item-count="totalItemCount"
            @fetch="$emit('fetch')"
            @jump-to-page="$emit('jump-to-page', $event)"
            @update-page-count="$emit('update-page-count', $event)"
        />
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {
        faCaretUp,
        faSpinner,
        faTimes,
    } from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import utils from '@imt/vue-toolbox/src/utils';
    import camelCase from 'lodash/camelCase';
    import isEmpty from 'lodash/isEmpty';
    import startCase from 'lodash/startCase';

    import {queryParamProps as footerQueryParamProps} from './LFooter.vue';
    import LFooter from './LFooter.vue';
    import LListItem from './LListItem.vue';
    import Toolbar from './LToolbar.vue';

    library.add(
        faCaretUp,
        faSpinner,
        faTimes
    );

    /**
     * @group List View
     * This is the component that you'll utilize for new list views. It displays the list as well as the `LFooter` below it.
     */
    export default {
        name: 'List',
        components: {
            FontAwesomeIcon,
            LFooter,
            LListItem,
            Toolbar,
        },
        props: {
            // Gets prefixed to all analytic attributes on elements that users interact with
            analyticsPrefix: {
                type: String,
                required: true,
            },
            // Array of objects that define what the column should display.
            // i.e.<br /><br />`{ key: '<key of thing to display on item>',
            // class: 'classes to add to column',
            // sortable: boolean,
            // sortBy: 'actual key to sort by',
            // component: ComponentName, options: {}, value() { return 'a calculated callback function'; } }`<br/><br />
            // *A special key of `options` will display the `list-item-action-buttons` slot contents.
            // If you provide a key that doesn't exist, you can either set a `value()` callback function
            // that returns a string to display or set `component` to a component that you'd like displayed and it will use it.
            columns: {
                type: Array,
                required: true,
            },
            // The message that appears when no items are provided in the list and loading is falsy.
            emptyMessage: {
                type: String,
                required: false,
                default: '',
            },
            // The message that appears when no items are provided in the list when searching and loading is falsy.
            emptySearchMessage: {
                type: String,
                required: false,
                default: '',
            },
            // Defines if the list is fetching data or not. Displays a loading indicator in the `Footer` component when `true`.
            fetching: {
                type: Boolean,
                required: false,
                default: false,
            },
            // An array of objects to be displayed in the list
            items: {
                type: Array,
                required: true,
            },
            // Total number of items to be paginated
            totalItemCount: {
                type: Number,
                required: true
            },
            // Defines if the view is loading or not. Displays loading UI when `true`.
            loading: {
                type: Boolean,
                required: true,
            },
            // A string of the model name that is being displayed in the list. Used in the default empty results messages.
            modelName: {
                type: String,
                required: true,
            },
            // Whether the search box in the toolbar should be hidden or not.
            hideSearchInput: {
                type: Boolean,
                required: false,
                default: false,
            },
            // An object with key/value of search parameters that reflect what's filtered in the list.
            searchFields: {
                type: Object,
                default() {
                    return {};
                },
            },
            // The query param name for the sort of the list.
            sortQueryParam: {
                type: String,
                default: 'sort',
            },
            // Whether the list header should use the offset to account for the admin header.
            stickyHeaderOffset: {
                type: Boolean,
                required: false,
                default: true,
            },
            // The query param name for text searching the list.
            textSearchQueryParam: {
                type: String,
                default: 'search',
            },
            // The wording for tooltips used within the list view. (They are: `search_field_names`, `search_field_display`, )
            tooltipWording: {
                type: Object,
                required: true,
            },
            ...footerQueryParamProps,
        },
        data() {
            return {
                search: '',
                sort: '',
            };
        },
        computed: {
            combinedSearchFields() {
                let searchFields = {...this.searchFields};

                if (this.search) {
                    searchFields[this.textSearchQueryParam] = this.search;
                }

                return searchFields;
            },
        },
        created() {
            this.setSort(this.$route.query.sort);
            this.textSearch(this.$route.query[this.textSearchQueryParam]);
            this.$emit('fetch');
        },
        methods: {
            textSearch(value) {
                this.search = value;
                if (this.$route.query[this.textSearchQueryParam] !== value) {
                    let newQuery = {...this.$route.query};

                    if (!value && Object.keys(this.$route.query).includes(this.textSearchQueryParam)) {
                        delete newQuery[this.textSearchQueryParam];
                    }

                    this.$router.push({query: {...newQuery, ...this.combinedSearchFields, page: '1'}}).catch(error => {
                        utils.console.log(error);
                    });
                }

                this.$emit('fetch');
                this.$emit('search-field-added', value, this.combinedSearchFields);
            },
            clearSearchValues() {
                this.textSearch();
                this.$emit('cleared-search-values');
            },
            camelCase,
            getDisplay(key, value) {
                return key in this.tooltipWording.search_field_display ? this.tooltipWording.search_field_display[key](value) : value;
            },
            isEmpty,
            sortColumn(column) {
                if (column.sortable) {
                    this.setSort(column.sortBy || column.key);
                    this.$router.push({query: {...this.$route.query, [this.sortQueryParam]: this.sort}}).catch(err => {
                        utils.console.log(err);
                    });

                    this.$emit('fetch');
                    this.$emit('column-sorted', this.sort);
                }
            },
            setSort(sort = '') {
                if (sort || this.sort) {
                    this.sort = sort === this.sort ? '-' + this.sort : sort;
                }
            },
            startCase,
        },
    };
</script>

<style
    lang="sass"
    scoped
>
    .sticky-top-offset
        top: 4.25rem

    .list
        display: flex
        flex-direction: column
        width: 100%

        .list-header
            background-color: var(--kc-gray-400)
            font-weight: bold
            max-width: 100%
            display: flex
            flex-direction: row

            > *
                padding: 0.75rem
                display: flex

                &.sortable-column-heading
                    display: flex

                    &:hover
                        cursor: pointer
                        background-color: #dddddd

                    .sort-direction
                        margin-left: 0.25rem
                        transition: transform 500ms

                        &.descending
                            transform: rotate(180deg)

                &.not-sortable-column-heading
                    display: flex

                    &:hover
                        cursor: default

            .options
                flex-grow: 0
                padding: 0.75rem
                text-align: right
                margin-left: auto
                justify-content: flex-end

        .list-items
            > *
                &:nth-child(even)
                    background-color: var(--kc-gray-200)

        .loading-indicator
            display: none

    .list-empty
        padding: 1rem 0 0 1.5rem

    .loading
        .loading-indicator
            font-size: 2rem
            width: 100%
            text-align: center
            padding: 3rem
            display: block

    .search-filter-list
        background-color: var(--kc-gray-400)

        .search-filter-item
            display: inline-block
            margin: 0.25rem

            .search-filter-type
                padding: 0.25rem 0.35rem
                border: 1px solid var(--kc-blue-400)
                border-radius: 0.25rem 0 0 0.25rem
                background-color: var(--kc-blue-400)
                color: var(--kc-gray-50)

            .search-filter-values
                padding: 0.25rem 0.35rem
                border: 1px solid var(--kc-gray-50)
                border-radius: 0 0.25rem 0.25rem 0
                background-color: var(--kc-gray-50)

    body.mode.dark
        .list-header,
        .list-header > div
            background-color: var(--kc-blue-800)

        .list-header
            .sortable-column-heading
                &:hover
                    background-color: var(--kc-blue-700)

        .list
            .list-items
                > *:nth-child(even)
                    background-color: var(--kc-blue-800)

        .search-filter-list
            background-color: var(--kc-blue-800)

            .search-filter-item
                .search-filter-values
                    border-color: var(--kc-blue-700)
                    background-color: var(--kc-blue-700)
</style>
